import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bisApi } from "../../bis-api-client";
import type { AthleteSearchParams } from "../../bis-api/people/athletes";

const BIS = "bis";

export function useAthleteSearch(q: string, options: Omit<AthleteSearchParams, "q"> = {}) {
	return useQuery({
		queryKey: [BIS, "athletes", "search", q, options],
		queryFn: () => bisApi.athletes.search(q, options),
		enabled: q.trim().length >= 2,
	});
}

export function useSetAthleteBirthYear(athleteId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (birthYear: number) => bisApi.athletes.setBirthYear(athleteId, birthYear),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: [BIS, "eligibility", "athlete", athleteId],
			}),
	});
}

export function useEligibility(birthYear: number | null, season?: number) {
	return useQuery({
		queryKey: [BIS, "eligibility", birthYear, season ?? null],
		queryFn: () => bisApi.eligibility.calculate(birthYear as number, season),
		enabled: typeof birthYear === "number" && Number.isFinite(birthYear),
	});
}

export function useAthleteEligibility(athleteId?: string, season?: number) {
	return useQuery({
		queryKey: [BIS, "eligibility", "athlete", athleteId, season ?? null],
		queryFn: () => bisApi.eligibility.forAthlete(athleteId as string, season),
		enabled: !!athleteId,
	});
}

export function useScheduleCollisions(params?: { from: string; to: string; ageCategory?: string }) {
	return useQuery({
		queryKey: [BIS, "collisions", "schedule", params],
		queryFn: () =>
			bisApi.collisions.schedule(params as { from: string; to: string; ageCategory?: string }),
		enabled: !!params,
	});
}

export function useDoubleRegistrations(tournamentId?: string) {
	return useQuery({
		queryKey: [BIS, "collisions", "duplicates", tournamentId],
		queryFn: () => bisApi.collisions.duplicates(tournamentId as string),
		enabled: !!tournamentId,
	});
}

export function useChampions(params?: { season?: number; ageCategory?: string }) {
	return useQuery({
		queryKey: [BIS, "champions", params ?? null],
		queryFn: () => bisApi.champions.list(params),
	});
}

export function useTournamentPredictions(tournamentId?: string) {
	return useQuery({
		queryKey: [BIS, "predictions", tournamentId],
		queryFn: () => bisApi.predictions.forTournament(tournamentId as string),
		enabled: !!tournamentId,
	});
}

export function useAthleteSnapshots(athleteId?: string, list?: string) {
	return useQuery({
		queryKey: [BIS, "snapshots", athleteId, list ?? null],
		queryFn: () => bisApi.snapshots.forAthlete(athleteId as string, list),
		enabled: !!athleteId,
	});
}

// Squad Planning Queries

export function useRoster(params?: { q?: string; sort?: string }) {
	return useQuery({
		queryKey: [BIS, "roster", params ?? null],
		queryFn: () => bisApi.roster.list(params),
	});
}

export function usePlayer(id?: string) {
	return useQuery({
		queryKey: [BIS, "roster", "player", id],
		queryFn: () => bisApi.roster.get(id as string),
		enabled: !!id,
	});
}

export function useSeasonPlan() {
	return useQuery({
		queryKey: [BIS, "plan"],
		queryFn: () => bisApi.plan.list(),
	});
}

export function useCalendar(params?: {
	q?: string;
	federation?: string;
	sheet?: string;
	sort?: string;
}) {
	return useQuery({
		queryKey: [BIS, "calendar", params ?? null],
		queryFn: () => bisApi.calendar.list(params),
	});
}

export function useTournamentEntries(tournamentId?: string) {
	return useQuery({
		queryKey: [BIS, "calendar", "entries", tournamentId],
		queryFn: () => bisApi.calendar.entries(tournamentId as string),
		enabled: !!tournamentId,
	});
}

export function useCamps() {
	return useQuery({
		queryKey: [BIS, "camps"],
		queryFn: () => bisApi.camps.list(),
	});
}

export function useCampSignups(campId?: string) {
	return useQuery({
		queryKey: [BIS, "camps", "signups", campId],
		queryFn: () => bisApi.camps.signups(campId as string),
		enabled: !!campId,
	});
}

export function useRefereeCourses(kind?: string) {
	return useQuery({
		queryKey: [BIS, "referees", kind ?? null],
		queryFn: () => bisApi.referees.list(kind),
	});
}

export function useBptFutures() {
	return useQuery({
		queryKey: [BIS, "futures"],
		queryFn: () => bisApi.futures.list(),
	});
}

export function useAgeClasses() {
	return useQuery({
		queryKey: [BIS, "classes"],
		queryFn: () => bisApi.classes.list(),
	});
}

export function useLoad(season?: number) {
	return useQuery({
		queryKey: [BIS, "load", season ?? null],
		queryFn: () => bisApi.load.forSeason(season),
	});
}

export function useTournamentSearch(q: string) {
	return useQuery({
		queryKey: [BIS, "tournaments", "search", q],
		queryFn: () => bisApi.tournaments.search(q),
		enabled: q.trim().length >= 2,
	});
}

// Squad Planning Mutations

export function useCreatePlayer() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: any) => bisApi.roster.create(body),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "roster"] }),
	});
}

export function useUpdatePlayer() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ playerId, body }: { playerId: string; body: any }) =>
			bisApi.roster.update(playerId, body),
		onSuccess: (_data, { playerId }) => {
			queryClient.invalidateQueries({ queryKey: [BIS, "roster"] });
			queryClient.invalidateQueries({
				queryKey: [BIS, "roster", "player", playerId],
			});
		},
	});
}

export function useDeletePlayer() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (playerId: string) => bisApi.roster.remove(playerId),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "roster"] }),
	});
}

export function useSetDvvLink() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ playerId, dvvId }: { playerId: string; dvvId: string | null }) =>
			bisApi.roster.setDvvLink(playerId, dvvId),
		onSuccess: (_data, { playerId }) => {
			queryClient.invalidateQueries({ queryKey: [BIS, "roster"] });
			queryClient.invalidateQueries({
				queryKey: [BIS, "roster", "player", playerId],
			});
		},
	});
}

export function useClearDvvLink() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (playerId: string) => bisApi.roster.clearDvvLink(playerId),
		onSuccess: (_data, playerId) => {
			queryClient.invalidateQueries({ queryKey: [BIS, "roster"] });
			queryClient.invalidateQueries({
				queryKey: [BIS, "roster", "player", playerId],
			});
		},
	});
}

export function useSyncDvvPlayer() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (playerId: string) => bisApi.roster.syncDvv(playerId),
		onSuccess: (_data, playerId) => {
			queryClient.invalidateQueries({ queryKey: [BIS, "roster"] });
			queryClient.invalidateQueries({
				queryKey: [BIS, "roster", "player", playerId],
			});
		},
	});
}

export function useSyncDvvAll() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => bisApi.roster.syncDvvAll(),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "roster"] }),
	});
}

export function useSearchScrapedAthletes(q: string, limit = 10) {
	return useQuery({
		queryKey: [BIS, "roster", "scraped-athletes", q, limit],
		queryFn: () => bisApi.roster.searchScrapedAthletes({ q, limit }),
		enabled: q.trim().length >= 2,
	});
}

export function useCreatePlanItem() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: any) => bisApi.plan.create(body),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "plan"] }),
	});
}

export function useUpdatePlanItem(itemId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: any) => bisApi.plan.update(itemId, body),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "plan"] }),
	});
}

export function useDeletePlanItem(itemId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => bisApi.plan.remove(itemId),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "plan"] }),
	});
}

export function useCreateTournament() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: any) => bisApi.calendar.create(body),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "calendar"] }),
	});
}

export function useUpdateTournament() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ tournamentId, body }: { tournamentId: string; body: any }) =>
			bisApi.calendar.update(tournamentId, body),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "calendar"] }),
	});
}

export function useDeleteTournament() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (tournamentId: string) => bisApi.calendar.remove(tournamentId),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "calendar"] }),
	});
}

export function useCreateEntry() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ tournamentId, body }: { tournamentId: string; body: any }) =>
			bisApi.calendar.createEntry(tournamentId, body),
		onSuccess: (_data, { tournamentId }) =>
			queryClient.invalidateQueries({
				queryKey: [BIS, "calendar", "entries", tournamentId],
			}),
	});
}

export function useUpdateEntry() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			tournamentId,
			entryId,
			body,
		}: {
			tournamentId: string;
			entryId: string;
			body: any;
		}) => bisApi.calendar.updateEntry(tournamentId, entryId, body),
		onSuccess: (_data, { tournamentId }) =>
			queryClient.invalidateQueries({
				queryKey: [BIS, "calendar", "entries", tournamentId],
			}),
	});
}

export function useDeleteEntry() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ tournamentId, entryId }: { tournamentId: string; entryId: string }) =>
			bisApi.calendar.removeEntry(tournamentId, entryId),
		onSuccess: (_data, { tournamentId }) =>
			queryClient.invalidateQueries({
				queryKey: [BIS, "calendar", "entries", tournamentId],
			}),
	});
}

export function useCreateCamp() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: any) => bisApi.camps.create(body),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "camps"] }),
	});
}

export function useUpdateCamp(campId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: any) => bisApi.camps.update(campId, body),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "camps"] }),
	});
}

export function useDeleteCamp(campId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => bisApi.camps.remove(campId),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "camps"] }),
	});
}

export function useCreateSignup(campId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: any) => bisApi.camps.createSignup(campId, body),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: [BIS, "camps", "signups", campId],
			}),
	});
}

export function useUpdateSignup(campId: string, signupId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: any) => bisApi.camps.updateSignup(campId, signupId, body),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: [BIS, "camps", "signups", campId],
			}),
	});
}

export function useDeleteSignup(campId: string, signupId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => bisApi.camps.removeSignup(campId, signupId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: [BIS, "camps", "signups", campId],
			}),
	});
}

export function useCreateRefereeCourse() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: any) => bisApi.referees.create(body),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "referees"] }),
	});
}

export function useUpdateRefereeCourse(courseId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: any) => bisApi.referees.update(courseId, body),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "referees"] }),
	});
}

export function useDeleteRefereeCourse(courseId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => bisApi.referees.remove(courseId),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "referees"] }),
	});
}

export function useCreateBptFuture() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: any) => bisApi.futures.create(body),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "futures"] }),
	});
}

export function useUpdateBptFuture(futureId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: any) => bisApi.futures.update(futureId, body),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "futures"] }),
	});
}

export function useDeleteBptFuture(futureId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => bisApi.futures.remove(futureId),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "futures"] }),
	});
}

export function useUpdateAgeClass(classId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: any) => bisApi.classes.update(classId, body),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [BIS, "classes"] }),
	});
}

// ---------------------------------------------------------------------------
// BI shell — record-list hooks (12 user-listed categories)
// ---------------------------------------------------------------------------

export interface RankingsHookParams {
	athlete?: string;
	rankingType?: string;
	ageGroup?: string;
	season?: number;
	minPoints?: number;
	limit?: number;
}

export function useRankings(params: RankingsHookParams = {}) {
	return useQuery({
		queryKey: [BIS, "rankings", params],
		queryFn: () => bisApi.rankings.list(params),
	});
}

export interface EntriesHookParams {
	tournament?: string;
	athlete?: string;
	status?: string;
	limit?: number;
}

export function useTournamentEntryList(params: EntriesHookParams = {}) {
	return useQuery({
		queryKey: [BIS, "entries", params],
		queryFn: () => bisApi.entries.list(params),
	});
}

export function useEntryLists(tournamentId?: string) {
	return useQuery({
		queryKey: [BIS, "entry-lists", tournamentId],
		queryFn: () => bisApi.entries.entryLists(tournamentId as string),
		enabled: !!tournamentId,
	});
}

export interface MatchesHookParams {
	tournament?: string;
	round?: string;
	limit?: number;
}

export function useMatches(params: MatchesHookParams = {}) {
	return useQuery({
		queryKey: [BIS, "matches", params],
		queryFn: () => bisApi.matches.list(params),
	});
}

export function useTournamentMatches(params: MatchesHookParams = {}) {
	return useQuery({
		queryKey: [BIS, "tournament-matches", params],
		queryFn: () => bisApi.matches.list(params),
	});
}

export function useEntitySnapshotsList(params: SnapshotsHookParams = {}) {
	return useQuery({
		queryKey: [BIS, "entity-snapshots-list", params],
		queryFn: () => bisApi.entitySnapshots.list(params),
	});
}

export interface ResultsHookParams {
	tournament?: string;
	athlete?: string;
	minPlacement?: number;
	limit?: number;
}

export function useResults(params: ResultsHookParams = {}) {
	return useQuery({
		queryKey: [BIS, "results", params],
		queryFn: () => bisApi.results.list(params),
	});
}

export interface DbmHookParams {
	season?: number;
	ageCategory?: string;
	gender?: string;
}

export function useDbm(params: DbmHookParams = {}) {
	return useQuery({
		queryKey: [BIS, "dbm", params],
		queryFn: () => bisApi.dbm.list(params),
	});
}

export interface InternationalHookParams {
	season?: number;
	tour?: string;
	limit?: number;
}

export function useInternational(params: InternationalHookParams = {}) {
	return useQuery({
		queryKey: [BIS, "international", params],
		queryFn: () => bisApi.international.list(params),
	});
}

export interface SnapshotsHookParams {
	entityType?: string;
	entityId?: string;
	since?: string;
	limit?: number;
}

export function useEntitySnapshots(params: SnapshotsHookParams = {}) {
	return useQuery({
		queryKey: [BIS, "entity-snapshots", params],
		queryFn: () => bisApi.entitySnapshots.list(params),
	});
}

// Per-federation scrape freshness for the records "Datenquelle" transparency panel.
export function useDataSources() {
	return useQuery({
		queryKey: [BIS, "sources"],
		queryFn: () => bisApi.sources.list(),
	});
}
